import React, { useState, useEffect, useRef, useId } from 'react';
import { api } from '../api';

/**
 * Busca de insumo com sugestões, aceitando também nome novo digitado.
 *
 * Antes o input não tinha rótulo nem aria-label (o `label` chegava `false` de
 * PainelLavoura) e as sugestões só respondiam ao clique: quem usava teclado não
 * conseguia escolher nenhuma. Agora é um combobox navegável por setas.
 *
 * `onChange(valor, { origem })` — `origem` é 'digitacao' quando o usuário está
 * escrevendo e 'selecao' quando ele escolheu um insumo do catálogo. Quem só usa
 * o primeiro argumento continua funcionando igual: era impossível para o pai
 * saber a diferença olhando só o valor (digitar sempre manda id null), e por
 * isso um simples ajuste de letra no nome apagava campos já preenchidos.
 */
export const ORIGEM_DIGITACAO = 'digitacao';
export const ORIGEM_SELECAO = 'selecao';

export default function InsumoAutocomplete({
  value, onChange, categoria = null, placeholder = 'Digite para buscar...',
  inputStyle, id, 'aria-describedby': descBy,
}) {
  const [termo, setTermo] = useState(value?.nome || '');
  const [sugestoes, setSugestoes] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(-1);
  const ref = useRef(null);
  const timer = useRef(null);
  const listaId = useId();
  const digitando = useRef(false);

  // Sincroniza campo quando o valor externo é limpo (após salvar, ao trocar de
  // categoria). O valor zerado que o próprio digitar produz não conta: ele
  // apagava o nome no meio da frase, no primeiro caractere corrigido.
  useEffect(() => {
    if (value) { digitando.current = false; return; }
    if (digitando.current) return;
    setTermo('');
  }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) fechar(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  // Evita busca pendente disparando depois que o componente saiu da tela.
  useEffect(() => () => clearTimeout(timer.current), []);

  const fechar = () => { setAberto(false); setAtivo(-1); };

  const handleChange = (e) => {
    const v = e.target.value;
    setTermo(v);
    digitando.current = true;
    // Informa pai: nome digitado (novo insumo), sem id
    onChange({ nome: v, id: null, unidadeMedida: null }, { origem: ORIGEM_DIGITACAO });
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setSugestoes([]); fechar(); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await api.buscarInsumos(v, categoria);
        setSugestoes(res);
        setAberto(res.length > 0);
        setAtivo(-1);
      } catch { setSugestoes([]); fechar(); }
    }, 300);
  };

  const selecionar = (insumo) => {
    setTermo(insumo.nome);
    digitando.current = false;
    onChange(insumo, { origem: ORIGEM_SELECAO }); // insumo existente — tem id, unidadeMedida, categoria
    setSugestoes([]);
    fechar();
  };

  const onKeyDown = (e) => {
    if (!aberto || sugestoes.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setAtivo(i => Math.min(i + 1, sugestoes.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setAtivo(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        if (ativo >= 0) { e.preventDefault(); selecionar(sugestoes[ativo]); }
        break;
      case 'Escape':
        e.preventDefault();
        fechar();
        break;
      default:
        break;
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        id={id}
        value={termo}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        style={inputStyle}
        role="combobox"
        aria-expanded={aberto}
        aria-controls={listaId}
        aria-autocomplete="list"
        aria-label={id ? undefined : 'Buscar insumo'}
        aria-describedby={descBy}
        aria-activedescendant={ativo >= 0 ? `${listaId}-${ativo}` : undefined}
      />
      {aberto && sugestoes.length > 0 && (
        <ul
          id={listaId}
          role="listbox"
          className="custom-select-options"
          style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1000 }}
        >
          {sugestoes.map((i, idx) => (
            <li
              key={i.id}
              id={`${listaId}-${idx}`}
              role="option"
              aria-selected={idx === ativo}
              className={`custom-select-option ${idx === ativo ? 'destacado' : ''}`}
              onMouseEnter={() => setAtivo(idx)}
              onClick={() => selecionar(i)}
            >
              <strong>{i.nome}</strong>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginLeft: '0.5rem' }}>
                {i.unidadeMedida}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
